/**
 * Costo real del producto (spec 16): costo de factura + gastos asignados por unidad, con y sin factura. Lectura para
 * la ficha del producto, los buscadores del reparto, la ganancia real de la consola y el cuadre entre Compras, Gastos
 * y el costo de los productos.
 */
import { z } from 'zod';
import { uuid } from './comun.ts';

export const busquedaCostosQuery = z.object({ q: z.string().trim().max(80).optional(), limite: z.coerce.number().int().min(1).max(30).default(10) });
export const rangoCostosQuery = z.object({ desde: z.iso.date(), hasta: z.iso.date(), sucursal_id: uuid.optional() });
export const gananciaRealQuery = rangoCostosQuery.extend({
  /** tienda = gastos de la tienda · no_tienda = gastos «no de la tienda» (personales de la dueña) · todos. */
  ambito: z.enum(['tienda', 'no_tienda', 'todos']).default('todos'),
});
export type GananciaRealQuery = z.infer<typeof gananciaRealQuery>;

/** Fila del reparto de un gasto entre productos, para la pantalla. */
export interface RepartoFilaDTO {
  producto_id: string;
  codigo: string;
  nombre: string;
  monto: string;
  unidades: string;
  /** Gasto por unidad: monto / unidades. */
  por_unidad: string;
}

/** Un gasto asignado a un producto, por origen (servicio con factura de una compra, o gasto sin factura). */
export interface GastoAsignadoDTO {
  id: string;
  origen: 'compra' | 'gasto';
  /** «Compra A-00000012» o «Gasto N° 34». */
  referencia: string;
  compra_id: string | null;
  gasto_id: string | null;
  concepto: string;
  con_factura: boolean;
  monto: string;
  unidades: string;
  por_unidad: string;
  estado: 'vigente' | 'reemplazado' | 'anulado';
  /** Suma hoy al costo real: vigente y anclado a la compra vigente del producto. */
  cuenta: boolean;
  creado_en: string;
  cerrado_en: string | null;
  motivo_cierre: string | null;
}

export interface FichaCostoDTO {
  producto_id: string;
  codigo: string;
  nombre: string;
  unidad: string;
  costo_factura: string;
  /** `sin_factura`: la compra vigente es una nota de entrega del proveedor (gasto no deducible del ISLR). */
  compra_vigente: { id: string; numero_formateado: string | null; proveedor: string; fecha_factura: string; sin_factura: boolean } | null;
  gastos_con_factura: string;
  gastos_sin_factura: string;
  gastos_por_unidad: string;
  costo_real: string;
  gastos: GastoAsignadoDTO[];
  margenes: { lista_id: string; lista: string; precio: string; margen: string; margen_pct: string }[];
  /** `sin_factura`: lo movió una compra con nota de entrega del proveedor. */
  historico: { ocurrido_en: string; costo_anterior: string | null; costo_nuevo: string; referencia_tipo: string | null; referencia: string | null; usuario_nombre: string | null; sin_factura: boolean }[];
}

/** Producto para el reparto: existencia actual (todas las ubicaciones) como unidades por defecto. */
export interface ProductoCostoDTO {
  id: string;
  codigo: string;
  nombre: string;
  unidad: string;
  existencia: string;
  costo_real: string;
}

/** Compra anterior para repartir un gasto entre lo que trajo: sus artículos y unidades. */
export interface CompraParaRepartirDTO {
  id: string;
  numero_formateado: string | null;
  proveedor_razon_social: string;
  numero_factura: string;
  fecha_factura: string;
  productos: { producto_id: string; codigo: string; nombre: string; cantidad: string; existencia: string }[];
}

/** Ganancia real (consola de la dueña): ventas − costo real de lo vendido − gastos generales. Interna, no fiscal. */
export interface GananciaRealDTO {
  desde: string;
  hasta: string;
  ambito: 'tienda' | 'no_tienda' | 'todos';
  ventas: string;
  costo_vendido: string;
  margen_bruto: string;
  /** Gastos generales sin factura (Gastos, no asociados a productos), según el filtro. */
  gastos_sin_factura: string;
  /** Gastos operativos con factura (servicios de Compras no cargados al costo): son de la tienda. */
  gastos_con_factura: string;
  gastos_generales: string;
  ganancia_real: string;
  /**
   * Lo vendido de productos con existencia que no tenían costo al venderse (costo 0: nunca se compraron ni se les cargó
   * costo). Va dentro de `ventas` con costo 0: la ganancia sale más alta de lo real. Solo informativo (2026-11-01).
   */
  ventas_sin_costo: string;
  productos_sin_costo: number;
}

/**
 * Cuadre (caso i): Σ servicios fiscales cargados al costo + Σ gastos sin factura = Σ gastos asignados a productos +
 * Σ gastos generales sin factura. Lo fiscal sale de Compras, lo no fiscal de Gastos; nada se suma dos veces.
 */
export interface CuadreCostosDTO {
  desde: string;
  hasta: string;
  servicios_cargados: string;
  gastos_sin_factura: string;
  asignados_con_factura: string;
  asignados_sin_factura: string;
  generales_sin_factura: string;
  izquierda: string;
  derecha: string;
  cuadra: boolean;
}
